import React, { useState, useEffect } from 'react';

const DEFAULTS = {
  enabled: true,
  storage: 'local',
  local_disk: true,
  local_memory: false,
  memory_size: '10M',
  remote_enabled: false,
  remote_server: '',
  remote_port: 514,
  remote_protocol: 'udp',
  remote_facility: 'local7',
  log_topics: {
    firewall: true,
    connection: true,
    dhcp: true,
    hotspot: false,
    system: true,
    interface: false,
    routing: false,
    wireless: false,
  },
  log_action: true,
  log_prefix: true,
};

const STORAGE_OPTIONS = [
  { value: 'local', label: '💾 เก็บในเครื่อง (Router)', desc: 'เก็บ Log ใน Flash/Memory ของ Router' },
  { value: 'remote', label: '🌐 เก็บใน Remote Server', desc: 'ส่ง Log ไปยัง Syslog Server ภายนอก' },
  { value: 'both', label: '💾🌐 เก็บทั้ง 2 ที่', desc: 'เก็บในเครื่องและส่งไป Remote พร้อมกัน' },
];

export default function StepLog({ data, onChange, onNext, onBack }) {
  const [form, setForm] = useState(data || DEFAULTS);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const update = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    onChange(updated);
  };

  const toggleTopic = (topic) => {
    const topics = { ...form.log_topics, [topic]: !form.log_topics[topic] };
    update('log_topics', topics);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">📝 ระบบ Log</h2>
        <p className="text-gray-500 dark:text-gray-400">ตั้งค่าการเก็บ Log การใช้งาน ไฟร์วอลล์ และการเชื่อมต่อ</p>
      </div>

      {/* Storage Location */}
      <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
        <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <span className="text-xl">📦</span> ที่เก็บ Log
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {STORAGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => update('storage', opt.value)}
              className={`p-3 rounded-xl border-2 text-left text-sm transition-all ${
                form.storage === opt.value
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-700'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
              }`}
            >
              <div className="font-bold">{opt.label}</div>
              <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Local Storage Settings */}
      {(form.storage === 'local' || form.storage === 'both') && (
        <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-3">💾 การเก็บในเครื่อง</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.local_disk} onChange={e => update('local_disk', e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">เก็บใน Flash/Disk</span>
                <p className="text-xs text-gray-400">เก็บถาวร ไม่หายเมื่อปิดเครื่อง</p>
              </div>
            </label>
            <label className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.local_memory} onChange={e => update('local_memory', e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">เก็บใน Memory</span>
                <p className="text-xs text-gray-400">เร็ว แต่หายเมื่อปิดเครื่อง</p>
              </div>
            </label>
          </div>
          {form.local_memory && (
            <div className="mt-3">
              <label className="label">ขนาด Memory (เช่น 10M, 50M, 100M)</label>
              <input type="text" value={form.memory_size} onChange={e => update('memory_size', e.target.value)} className="input-field" placeholder="10M" />
            </div>
          )}
        </div>
      )}

      {/* Remote Syslog Settings */}
      {(form.storage === 'remote' || form.storage === 'both') && (
        <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-3">🌐 Remote Syslog Server</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">IP/Hostname ของ Syslog Server</label>
              <input type="text" value={form.remote_server} onChange={e => update('remote_server', e.target.value)} className="input-field" placeholder="192.168.1.100 หรือ syslog.example.com" />
            </div>
            <div>
              <label className="label">พอร์ต</label>
              <input type="number" value={form.remote_port} onChange={e => update('remote_port', parseInt(e.target.value) || 514)} className="input-field" placeholder="514" />
            </div>
            <div>
              <label className="label">โปรโตคอล</label>
              <select value={form.remote_protocol} onChange={e => update('remote_protocol', e.target.value)} className="input-field">
                <option value="udp">UDP (เร็ว, ไม่รับประกัน)</option>
                <option value="tcp">TCP (เชื่อถือได้)</option>
              </select>
            </div>
            <div>
              <label className="label">Facility</label>
              <select value={form.remote_facility} onChange={e => update('remote_facility', e.target.value)} className="input-field">
                <option value="local0">local0</option>
                <option value="local1">local1</option>
                <option value="local2">local2</option>
                <option value="local3">local3</option>
                <option value="local4">local4</option>
                <option value="local5">local5</option>
                <option value="local6">local6</option>
                <option value="local7">local7</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Log Topics */}
      <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
        <h3 className="font-bold text-gray-900 dark:text-white mb-3">📂 ประเภท Log ที่ต้องการเก็บ</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { key: 'firewall', label: '🛡️ Firewall', desc: 'กฎไฟร์วอลล์' },
            { key: 'connection', label: '🔗 Connection', desc: 'การเชื่อมต่อ' },
            { key: 'dhcp', label: '📡 DHCP', desc: 'แจก IP' },
            { key: 'hotspot', label: '📶 Hotspot', desc: 'ผู้ใช้ Hotspot' },
            { key: 'system', label: '⚙️ System', desc: 'ระบบ Router' },
            { key: 'interface', label: '🔧 Interface', desc: 'พอร์ตขึ้น/ลง' },
            { key: 'routing', label: '🗺️ Routing', desc: 'เส้นทาง' },
            { key: 'wireless', label: '📶 Wireless', desc: 'WiFi' },
          ].map(topic => (
            <button
              key={topic.key}
              onClick={() => toggleTopic(topic.key)}
              className={`p-3 rounded-lg border-2 text-left text-xs transition-all ${
                form.log_topics[topic.key]
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400'
              }`}
            >
              <div className="font-bold">{topic.label}</div>
              <div className="opacity-70 text-[10px] mt-0.5">{topic.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Additional Options */}
      <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
        <h3 className="font-bold text-gray-900 dark:text-white mb-3">⚙️ ตัวเลือกเพิ่มเติม</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.log_action} onChange={e => update('log_action', e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Log Action ใน Firewall</span>
              <p className="text-xs text-gray-400">เพิ่ม action=log ในกฎไฟร์วอลล์</p>
            </div>
          </label>
          <label className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.log_prefix} onChange={e => update('log_prefix', e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">เพิ่ม Prefix ใน Log</span>
              <p className="text-xs text-gray-400">เช่น [FIREWALL] [DROP]</p>
            </div>
          </label>
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป</button>
      </div>
    </div>
  );
}
